import React, { useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import styles from './SegmentedControl.module.scss'

export interface SegmentedControlOption {
  key: string
  label: string
  count?: number
  hideCount?: boolean
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[]
  activeKey: string
  ariaLabel: string
  onSelect: (key: string) => void
}

interface IndicatorStyle {
  left: number
  top: number
  width: number
  height: number
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, activeKey, ariaLabel, onSelect }) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicator, setIndicator] = useState<IndicatorStyle | null>(null)

  const activeIndex = options.findIndex((option) => option.key === activeKey)
  const optionsSignature = options.map((option) => `${option.label}:${option.count}:${option.hideCount}`).join(',')
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? buttonRefs.current[activeIndex] : null
      setIndicator(el ? { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight } : null)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [activeIndex, optionsSignature])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const last = options.length - 1
    const current = activeIndex < 0 ? 0 : activeIndex
    let nextIndex: number
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = current >= last ? 0 : current + 1
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = current <= 0 ? last : current - 1
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = last
        break
      default:
        return
    }
    e.preventDefault()
    onSelect(options[nextIndex].key)
    buttonRefs.current[nextIndex]?.focus()
  }

  return (
    <div
      role='tablist'
      aria-label={ariaLabel}
      className={classNames('position-relative d-flex gap-1 align-items-center zocks-bg-color-gray-100 zocks-border-color-gray-200 border p-1', styles.segment)}
      onKeyDown={handleKeyDown}
    >
      {indicator ? (
        <div
          className={styles.segmentIndicator}
          style={{ transform: `translateX(${indicator.left}px)`, top: indicator.top, width: indicator.width, height: indicator.height }}
        />
      ) : null}
      {options.map((option, index) => {
        const active = option.key === activeKey
        return (
          <button
            key={option.key}
            ref={(el) => {
              buttonRefs.current[index] = el
            }}
            type='button'
            role='tab'
            aria-selected={active}
            tabIndex={active || (activeIndex < 0 && index === 0) ? 0 : -1}
            className={classNames('position-relative d-flex gap-1 align-items-center px-2 py-1 paragraph-small bold', styles.segmentButton, {
              'zocks-text-gray-900': active,
              'zocks-text-gray-600': !active,
            })}
            onClick={() => onSelect(option.key)}
          >
            <span>{option.label}</span>
            {option.count !== undefined && !option.hideCount ? <span className='zocks-text-gray-500'>{option.count}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

export default SegmentedControl
